import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';
import { useLanguage } from '../context/LanguageContext';
import LanguageToggle from '@/components/LanguageToggle';
import SEO from '@/components/SEO';

const { FiArrowLeft, FiClock, FiMapPin, FiAward } = FiIcons;

export default function ArticleLaBarberenca() {
  const navigate = useNavigate();
  const { lang, t } = useLanguage();

  const articleImages = {
    glass: "https://media-manager-c.questera.ai/greta-media/6f319949afba45b9008649532004af25bb95492e2b2fb555f5bdcdad5193a9e70a9e6d92944cfc73f8b57561372ade93/images/aW1hZ2UvanBlZw==/5fd6fd488837ac796e252f0ab6ecdeb5.jpeg",
    hops: "https://media-manager-c.questera.ai/greta-media/6f319949afba45b9008649532004af25bb95492e2b2fb555f5bdcdad5193a9e70a9e6d92944cfc73f8b57561372ade93/images/aW1hZ2UvanBlZw==/309046e5e08be9ab1ca16b4b0c3f9d89.jpeg",
    pour: "https://media-manager-c.questera.ai/greta-media/6f319949afba45b9008649532004af25bb95492e2b2fb555f5bdcdad5193a9e70a9e6d92944cfc73f8b57561372ade93/images/aW1hZ2UvanBlZw==/54607623f0a9d45431b8375bf8a1127e.jpeg"
  };

  // Fallback content in case translations.js isn't updated
  const fallback = {
    en: {
      title: "Inside La Barberenca: The Heart of Craft Beer",
      subtitle: "Brewpub Review · Barberà del Vallès",
      intro: "In the industrial heart of Barberà del Vallès, a revolution is brewing. As the craft beer movement sweeps across Catalonia, one local factory has become a sanctuary for purists. La Barberenca isn't just a bar; it's a testament to the art of the fermentation, standing as a beacon for those who seek depth over volume.",
      body1: "Stepping into the taproom, you're immediately struck by the raw, industrial honesty of the space. There are no pretenses here. Through the curtain partitions, the stainless steel fermentation tanks gleam under soft lights - a constant reminder that the beer you're drinking was born exactly where you stand. The atmosphere balances the grit of a working brewery with the warmth of a community hub.",
      body2: "The magic, however, lies in the freshness. Pouring directly from the tap ensures a profile that bottled alternatives simply cannot match. Whether it's the crisp, clean notes of 'La Llibertaria' (their signature German Pils) or the complex, resinous punch of their rotating IPAs, each pint is a masterclass in balance and temperature control.",
      body3: "For the residents of Barberà and visitors from Barcelona alike, the verdict is unanimous. By stripping away the fluff and focusing on technical excellence, La Barberenca has secured its place as the undisputed number one craft beer destination in the region. It is, quite simply, where the soul of the valley meets the craft of the hop.",
      backBtn: "Back to Journal",
      readTime: "4 min read",
      location: "Carrer de Nemesi Valls, 24"
    },
    es: {
      title: "Dentro de La Barberenca: El Corazón de la Cerveza Artesana",
      subtitle: "Reseña de Brewpub · Barberà del Vallès",
      intro: "En el corazón industrial de Barberà del Vallès, se está fraguando una revolución. Mientras el movimiento de la cerveza artesana recorre Cataluña, una fábrica local se ha convertido en un santuario para los puristas. La Barberenca no es solo un bar; es un testimonio del arte de la fermentación, un faro para quienes buscan profundidad en lugar de volumen.",
      body1: "Al entrar en la sala de grifos, te sorprende de inmediato la honestidad industrial y cruda del espacio. Aquí no hay pretensiones. A través de los paneles de cristal, los tanques de fermentación de acero inoxidable brillan bajo luces tenues, un recordatorio constante de que la cerveza que bebes nació exactamente donde estás parado. El ambiente equilibra la rudeza de una fábrica con la calidez de un centro comunitario.",
      body2: "La magia, sin embargo, reside en la frescura. Servir directamente al grifo garantiza un perfil que las alternativas embotelladas simplemente no pueden igualar. Ya sea por las notas nítidas y limpias de 'La Llibertaria' (su emblemática German Pils) o el golpe complejo y resinoso de sus IPAs rotativas, cada pinta es una lección magistral de equilibrio y control de temperatura.",
      body3: "Tanto para los residentes de Barberà como para los visitantes de Barcelona, el veredicto es unánime. Al eliminar lo innecesario y centrarse en la excelencia técnica, La Barberenca ha asegurado su lugar como el destino indiscutible de cerveza artesana en la región. Es, sencillamente, donde el alma del valle se encuentra con el oficio del lúpulo.",
      backBtn: "Volver al Diario",
      readTime: "4 min de lectura",
      location: "Carrer de Nemesi Valls, 24"
    }
  };

  const content = lang === 'es' ? fallback.es : fallback.en;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": content.title,
    "description": content.intro,
    "image": "https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1779688358482-blob",
    "author": {
      "@type": "Organization",
      "name": "The Selection Barberà"
    },
    "publisher": {
      "@type": "Organization",
      "name": "The Selection",
      "logo": {
        "@type": "ImageObject",
        "url": "https://barberadelvalles.bar/logo.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://barberadelvalles.bar/journal/la-barberenca"
    },
    "about": {
      "@type": "Brewery",
      "name": "La Barberenca",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Carrer de Nemesi Valls, 24",
        "addressLocality": "Barberà del Vallès",
        "postalCode": "08210",
        "addressCountry": "ES"
      }
    }
  };

  return (
    <div className="min-h-screen bg-black text-zinc-400 selection:bg-zinc-100 selection:text-zinc-950">
      <SEO 
        title={`${content.title} | Barberà del Vallès`} 
        description={content.intro.substring(0, 160)} 
        schema={articleSchema}
      />
      <LanguageToggle />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-zinc-900/50 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <button 
            onClick={() => navigate('/journal')}
            className="group flex items-center gap-3 text-zinc-500 hover:text-zinc-50 transition-all duration-500"
          >
            <div className="w-8 h-8 rounded-full border border-zinc-800 flex items-center justify-center group-hover:border-zinc-500 transition-colors">
              <SafeIcon icon={FiArrowLeft} className="text-sm group-hover:-translate-x-0.5 transition-transform" />
            </div>
            <span className="text-[10px] font-bold tracking-[0.3em] uppercase">{content.backBtn}</span>
          </button>
          
          <div className="hidden md:flex items-center gap-6 text-[10px] font-bold tracking-[0.2em] text-zinc-600 uppercase">
            <span className="flex items-center gap-2"><SafeIcon icon={FiClock} /> {content.readTime}</span>
            <span className="flex items-center gap-2"><SafeIcon icon={FiMapPin} /> Barberà</span>
          </div>
        </div>
      </nav>

      <main className="pt-16">
        {/* Full-width Hero */}
        <section className="relative w-full h-[70vh] md:h-[85vh] overflow-hidden">
          <motion.img 
            initial={{ scale: 1.1, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1.5, ease: "easeOut" }}
            src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1779688358482-blob" 
            alt="La Barberenca Brewery"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
          
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-20 max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              <span className="inline-block px-3 py-1 bg-zinc-50 text-black text-[10px] font-black uppercase tracking-[0.3em] mb-6">
                {content.subtitle}
              </span>
              <h1 className="text-5xl md:text-8xl font-bold text-white tracking-tighter leading-[0.9] max-w-4xl">
                {content.title}
              </h1>
            </motion.div>
          </div>
        </section>

        {/* Article Content */}
        <article className="max-w-prose mx-auto px-6 py-24 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xl md:text-2xl text-zinc-200 font-light leading-relaxed mb-16 first-letter:text-7xl first-letter:font-bold first-letter:text-white first-letter:mr-3 first-letter:float-left">
              {content.intro}
            </p>

            <div className="space-y-12 text-lg leading-relaxed font-light">
              <p>{content.body1}</p>

              {/* Added Image: Pouring */}
              <motion.figure
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="w-full my-16"
              >
                <img
                  src={articleImages.pour}
                  alt="Pouring fresh craft beer from the tank"
                  className="w-full h-auto max-h-[70vh] object-cover rounded-[2rem] shadow-2xl border border-zinc-800/50"
                />
                <figcaption className="text-center text-[10px] text-zinc-500 mt-6 tracking-[0.2em] uppercase font-bold">
                  {lang === 'es' ? 'Fresco desde el origen' : 'Fresh from the source'}
                </figcaption>
              </motion.figure>
              
              <div className="py-12 flex flex-col items-center">
                <div className="w-12 h-px bg-zinc-800 mb-8" />
                <SafeIcon icon={FiAward} className="text-3xl text-zinc-100 mb-4" />
                <blockquote className="text-center text-zinc-100 italic text-xl font-medium max-w-md">
                  {lang === 'es' 
                    ? '"Es, sencillamente, donde el alma del Vallès se encuentra con el oficio del lúpulo."'
                    : '"It is, quite simply, where the soul of the Vallès meets the craft of the hop."'}
                </blockquote>
                <div className="w-12 h-px bg-zinc-800 mt-8" />
              </div>

              <p>{content.body2}</p>

              {/* Added Images: Grid with Glass and Hops */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-20">
                <motion.figure
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                >
                  <img
                    src={articleImages.glass}
                    alt="La Barberenca Craft Beer Glass"
                    className="w-full aspect-[4/5] object-cover rounded-[2rem] shadow-2xl border border-zinc-800/50"
                  />
                </motion.figure>
                <motion.figure
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="md:mt-12"
                >
                  <img
                    src={articleImages.hops}
                    alt="Hops and Ingredients"
                    className="w-full aspect-[4/5] object-cover rounded-[2rem] shadow-2xl border border-zinc-800/50"
                  />
                </motion.figure>
              </div>

              <p>{content.body3}</p>
            </div>

            {/* Footer metadata */}
            <footer className="mt-24 pt-12 border-t border-zinc-900 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
              <div>
                <p className="text-[10px] font-bold tracking-[0.3em] text-zinc-600 uppercase mb-2">Location</p>
                <p className="text-zinc-300">{content.location}</p>
              </div>
              <button 
                onClick={() => navigate('/menu/craftbeer')}
                className="bg-zinc-50 text-black px-8 py-4 text-[10px] font-bold uppercase tracking-widest hover:bg-white transition-colors"
              >
                {lang === 'es' ? 'Ver Grifos Actuales' : 'View Live Taps'}
              </button>
            </footer>
          </motion.div>
        </article>
      </main>

      {/* Recommended Footer */}
      <section className="bg-zinc-950 py-24 border-t border-zinc-900">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-[10px] font-bold tracking-[0.5em] text-zinc-600 uppercase mb-4">The Collection</p>
          <h2 className="text-3xl text-zinc-50 font-bold mb-12 tracking-tight">Barberà del Vallès, Barcelona</h2>
          <div className="w-px h-24 bg-gradient-to-b from-zinc-800 to-transparent mx-auto" />
        </div>
      </section>
    </div>
  );
}